/*
 * @Author: alex
 * @Date: 2022-11-01 13:52:23
 * @LastEditors: Alex yxfacw@163.com
 * @LastEditTime: 2023-05-23 09:57:55
 * @Description:
 * Copyright (c) 2022 by piesat, All Rights Reserved.
 */

import type {
  MapStyle,
  RasterTileStyleType,
  WebGLVectorStyleType,
  MapSourceType,
  MapLayerType,
  VectorFeatureStyleType,
  ImageStyleType
} from '../mapSpecification'
import { error } from '../console'
import {
  VectorTile as VectorTileLayer,
  Tile as TileLayer,
  Vector as VectorLayer,
  WebGLTile as WebGLTileLayer
} from 'ol/layer'
import BaseLayer from 'ol/layer/Base'
import {
  XYZ,
  TileArcGISRest,
  VectorTile,
  Vector,
  GeoTIFF,
  TileDebug,
  WMTS
} from 'ol/source'
import { MVT, GeoJSON } from 'ol/format'
import { WebGLVectorLayer } from './webGLVectorLayer'

import { Circle, Fill, Stroke, Style, Icon, Text } from 'ol/style'
import TileGrid from 'ol/tilegrid/TileGrid'
import WMTSTileGrid from 'ol/tilegrid/WMTS'
import { FeatureLike } from 'ol/Feature'
import { getBottomLeft, getHeight, getWidth } from 'ol/extent'
import { toContext } from 'ol/render'
import { Polygon } from 'ol/geom'
import { Coordinate } from 'ol/coordinate'
import { ShapfileLayer } from './shpLayer'
import { isNumber } from '@vmap/core'

/**
 * 数据源枚举
 */
export enum SourceTypes {
  XYZ = 'xyz',
  TileArcGISRest = 'arcgis-rest',
  WMTS = 'wmts',
  // 矢量切片
  VectorTile = 'vector-tile',
  // geojson
  Vector = 'vector',
  // arcgis 本地切片
  ArcGISLocal = 'arcgis-local',
  GeoTIFF = 'geotiff'
}

/**
 * 图层类型枚举
 */
export enum LayerTypes {
  WebGLTile = 'webgl-tile',
  Tile = 'tile',
  VectorTile = 'vector-tile',
  Heatmap = 'heatmap',
  // geojson layer
  Vector = 'vector',
  WebGLVector = 'webgl-vector',
  Image = 'image',
  // shp 图层
  Shp = 'shp'
}

/**
 * 图层加载器
 */
export class MapLayerLoader {
  constructor(private readonly mapStyle: MapStyle) {}

  load(): any[] {
    const layers = new Array<any>()
    const sources = this.mapStyle.sources
    this.mapStyle.layers.forEach(async layer => {
      const sourceType = sources[layer.source ?? 'unkonwn']
      layers.push(this.toOlLayer(layer, sourceType))
    })
    // debug
    if (this.mapStyle.debug) {
      layers.push(
        new TileLayer({
          source: new TileDebug({
            projection: this.mapStyle.crs ?? 'EPSG:3857'
          }),
          zIndex: 99999999
        })
      )
    }

    return layers
  }

  /**
   * convert to layer
   * @param layerType 图层类型
   * @param sourceType 数据源类型
   * @returns openlayers layer
   */
  toOlLayer(
    layerType: MapLayerType,
    sourceType: MapSourceType
  ): BaseLayer | ShapfileLayer | null {
    if (sourceType === undefined && layerType.type !== LayerTypes.Shp) {
      error(
        `err: source [${layerType.source}] 不存在, 跳过图层 [${layerType.name}]`
      )
      return null
    } else {
      const tileSource = sourceType ? this.getTileSource(sourceType) : undefined
      if (
        tileSource === undefined &&
        (layerType.type === LayerTypes.Tile ||
          layerType.type === LayerTypes.VectorTile)
        // sourceType.type !== SourceTypes.Vector &&
        // sourceType.type !== SourceTypes.GeoTIFF &&
        // layer.type !== LayerTypes.Shp
      ) {
        //
        error('error: Tile layer need source')
        return null
      }
      switch (layerType.type) {
        case LayerTypes.Shp:
          {
            const shpLyr = new ShapfileLayer(layerType.id, {
              url: layerType.url,
              crs: layerType.crs,
              ...layerType
            })
            return shpLyr
          }
          break
        case LayerTypes.WebGLVector:
          // TODO: webgl 渲染矢量图层
          return new WebGLVectorLayer({
            ...layerType,
            ...{
              style: layerType.style as WebGLVectorStyleType
            },
            ...{
              source: new Vector({
                url: sourceType.url,
                format: new GeoJSON()
              })
            }
          })
        case LayerTypes.WebGLTile:
          return new WebGLTileLayer({
            ...layerType,
            source: new GeoTIFF({
              sources: [{ ...sourceType }],
              transition: 0,
              // opaque: true,
              normalize: false
            }),
            style: layerType.style as RasterTileStyleType
          })
        case LayerTypes.Tile:
          return new TileLayer({
            ...layerType,
            ...{ source: tileSource }
          })
        case LayerTypes.VectorTile:
          if (tileSource instanceof VectorTile) {
            return new VectorTileLayer({
              ...layerType,
              ...{
                // 防止标注文字重复标注
                declutter: true,
                source: tileSource
              },
              style: feature => {
                return MapLayerLoader.getVectorStyle(
                  feature,
                  (layerType.style as VectorFeatureStyleType) ?? undefined
                )
              }
            })
          }
          break
        case LayerTypes.Vector: {
          const vectorLyr = new VectorLayer({
            ...layerType,
            ...{
              // 图片填充样式需要单独处理
              style: (layerType.style as VectorFeatureStyleType).imageSample
                ? new Style({
                    renderer: function (pixelCoordinates, state) {
                      const context = state.context
                      const geometry = state.geometry.clone() as Polygon
                      geometry.setCoordinates(
                        pixelCoordinates as Coordinate[][]
                      )
                      const extent = geometry.getExtent()
                      const width = getWidth(extent)
                      const height = getHeight(extent)
                      const flag = state.feature.get('styleImg')
                      if (!flag || height < 1 || width < 1) {
                        return
                      }

                      // Stitch out country shape from the blue canvas
                      context.save()
                      const renderContext = toContext(context, {
                        pixelRatio: 1
                      })
                      renderContext.setFillStrokeStyle(
                        new Fill(),
                        new Stroke({
                          color: 'rgba(255,255,255,0.8)',
                          width: 2
                        })
                      )
                      renderContext.drawGeometry(geometry)
                      context.clip()

                      // Fill transparent country with the flag image
                      const bottomLeft = getBottomLeft(extent)
                      const left = bottomLeft[0]
                      const bottom = bottomLeft[1]
                      context.drawImage(flag, left, bottom, width, height)
                      context.restore()
                    }
                  })
                : feature =>
                    MapLayerLoader.getVectorStyle(
                      feature,
                      (layerType.style as VectorFeatureStyleType) ?? undefined
                    )
            },
            ...{
              source: new Vector({
                url: sourceType.url,
                format: sourceType.projection
                  ? new GeoJSON({
                      featureProjection: sourceType.projection
                    })
                  : new GeoJSON()
              })
            }
          })
          const styleType = layerType.style as VectorFeatureStyleType
          // image 填充样式
          vectorLyr.getSource()?.on('addfeature', function (event) {
            const feature = event.feature
            if (feature) {
              const properties = feature.getProperties()
              const colorSample = styleType.imageSample ?? []
              const propVal = properties[styleType.fill?.propertyName ?? '']
              if (propVal !== undefined) {
                const hitted = colorSample.find(cs => cs.value == propVal)
                if (hitted) {
                  const img = new Image()
                  img.onload = function () {
                    feature.set('styleImg', img)
                  }
                  img.src = hitted.imgUrl
                }
              }
            }
          })
          return vectorLyr
        }
        default:
          break
      }
    }
    return null
  }

  /**
   * 根据配置生成对应的 ol 的切片类 source
   * @param sourceType 图层来源类型定义
   * @returns
   */
  private getTileSource(sourceType: MapSourceType) {
    switch (sourceType.type) {
      case SourceTypes.WMTS:
        return new WMTS({
          url: sourceType.url,
          layer: sourceType.layerName ?? '',
          matrixSet: sourceType.matrixSet ?? '',
          format: 'image/png',
          projection: sourceType.projection,
          tileGrid: new WMTSTileGrid({
            tileSize: [sourceType.tileSize ?? 256, sourceType.tileSize ?? 256],
            // extent: sourceType.extent ?? [-180.0, -90.0, 180.0, 90.0],
            origin: sourceType.origin ?? [-180.0, 90.0],
            resolutions: sourceType.resolutions ?? [],
            matrixIds: sourceType.matrixIds ?? ['']
          }),
          style: sourceType.style ?? '',
          wrapX: true
        })
      case SourceTypes.XYZ:
        return new XYZ(sourceType)
      case SourceTypes.TileArcGISRest:
        return new TileArcGISRest(sourceType)
      case SourceTypes.VectorTile:
        return new VectorTile({
          ...sourceType,
          ...{
            format: new MVT(),
            wrapX: true,
            tileUrlFunction: tileCoord => {
              return sourceType.url
                .replace('{z}', `${tileCoord[0]}`)
                .replace('{x}', `${tileCoord[1]}`)
                .replace('{y}', `${tileCoord[2]}`)
            }
          }
        })
      case SourceTypes.ArcGISLocal: {
        // 瓦片网格
        const tileGrid = new TileGrid({
          tileSize: sourceType.tileSize,
          origin: sourceType.origin,
          resolutions: sourceType.resolutions ?? []
        })
        return new XYZ({
          projection: sourceType.projection,
          tileGrid: tileGrid,
          crossOrigin: 'Anonymous',
          tileUrlFunction: coordinate => {
            const z = 'L' + this.zeroFill(coordinate[0], 2, 10)
            const x = 'C' + this.zeroFill(coordinate[1], 8, 16)
            const y = 'R' + this.zeroFill(coordinate[2], 8, 16)
            return sourceType.url + '/' + z + '/' + y + '/' + x + '.png' // 这里可以修改地图路径
          }
        })
      }
      default:
        return undefined
    }
  }

  // private getQueryValue(url: string, param: string): string | undefined {
  //   const arr = url.split('?')
  //   if (arr.length < 2) {
  //     return undefined
  //   }
  //   const paramStr = arr[1]
  //   const paramPairs = paramStr.split('&')
  //   for (let i = 0; i < paramPairs.length; i++) {
  //     const str = paramPairs[i]
  //     const tmp = str.split('=')
  //     if (tmp[0] == param.toLowerCase()) {
  //       return tmp[1]
  //     }
  //   }
  //   return undefined
  // }

  /**
   * 转换为 ol的样式对象
   * 1. 图片样式
   * 2. 文字样式
   * @param style  样式定义
   * @returns
   */
  static getVectorStyle(
    feature: FeatureLike,
    style: VectorFeatureStyleType | undefined
  ) {
    const fillDefault: Fill = new Fill({
      color: [0, 255, 255, 0.8]
    })
    const strokeDefault = new Stroke({
      color: '#3399CC',
      width: 1.25
    })
    const imageDefault = new Circle({
      radius: 6,
      fill: new Fill({
        color: [0, 153, 255, 1]
      }),
      stroke: new Stroke({
        color: [255, 255, 255, 1],
        width: 3 / 2
      })
    })
    const textDefault = new Text({
      textBaseline: 'middle',
      rotateWithView: true
    })

    if (style === undefined) {
      return new Style({
        fill: fillDefault,
        stroke: strokeDefault,
        text: textDefault
      })
    }
    const userDefined = new Style({
      fill:
        style.fill === undefined
          ? fillDefault
          : new Fill({ color: style.fill.color }),
      stroke:
        style.stroke === undefined
          ? strokeDefault
          : new Stroke({
              color: style.stroke.color,
              width: style.stroke.width,
              lineCap: style.stroke.lineCap ?? 'round',
              lineJoin: style.stroke.lineJoin ?? 'round',
              miterLimit: style.stroke.miterLimit ?? 10
            }),
      text:
        style.text === undefined
          ? textDefault
          : new Text({
              textAlign: style.text.textAlign ?? 'center',
              placement: style.text.placement ?? 'point',
              font: style.text.font ?? '10px sans-serif',
              textBaseline: style.text.textBaseline ?? 'middle',
              rotateWithView: true,
              backgroundFill: style.text.backgroundFill
                ? new Fill(style.text.backgroundFill)
                : undefined,
              backgroundStroke: style.text.backgroundStroke
                ? new Stroke(style.text.backgroundStroke)
                : undefined,
              fill: style.text.fill ? new Fill(style.text.fill) : undefined,
              stroke: style.text.stroke
                ? new Stroke(style.text.stroke)
                : undefined
            }),
      image:
        style.image === undefined
          ? imageDefault
          : this.getImageStyle(style.image)
    })

    if (style.fill && style.fill.propertyName !== undefined) {
      // 根据要素属性进行采样填色
      const properties = feature.getProperties()
      const colorSample = style.colorSample ?? []
      const propVal = properties[style.fill?.propertyName ?? '']
      if (propVal !== undefined) {
        const hitted = colorSample.find(cs => cs.value == propVal)
        if (hitted) {
          userDefined.getFill().setColor(hitted?.color ?? '#eee')
        }
      }
    }

    // 根据要素属性自定义显示文字
    if (style.text && style.text.propertyName !== undefined) {
      const properties = feature.getProperties()
      const label = properties[style.text?.propertyName ?? 'name']
      if (label !== undefined) {
        // set text
        userDefined.getText().setText(label)
        if (style.text?.showLabelOnly) {
          return new Style({ text: userDefined.getText() })
        }
      }
      // return userDefined
    }
    // 根据属性值获取不同的 radius
    if (
      style.image?.type === 'circle' &&
      style.image.radiusProp !== undefined
    ) {
      const properties = feature.getProperties()
      const radius = parseFloat(properties[style.image.radiusProp ?? 'radius'])
      const ratio = style.image.radiusRatio ?? 1
      if (radius && isNumber(radius)) {
        ;(userDefined.getImage() as Circle).setRadius(radius * ratio)
      }
      // return userDefined
    }

    return userDefined
  }

  static getImageStyle(imageType: ImageStyleType): Icon | Circle | undefined {
    switch (imageType.type) {
      case 'circle':
        return new Circle({
          radius: imageType.radius ?? 4,
          fill:
            imageType.fill === undefined
              ? new Fill({
                  color: [0, 153, 255, 1]
                })
              : new Fill({
                  color: imageType.fill.color
                }),
          stroke:
            imageType.stroke === undefined
              ? new Stroke({
                  color: [255, 255, 255, 1],
                  width: 3 / 2
                })
              : new Stroke({
                  color: imageType.stroke.color,
                  width: imageType.stroke.width,
                  lineCap: imageType.stroke.lineCap ?? 'round',
                  lineJoin: imageType.stroke.lineJoin ?? 'round',
                  miterLimit: imageType.stroke.miterLimit ?? 10
                })
        })
      case 'icon':
        return new Icon({
          anchor: imageType.anchor ?? [0.5, 1],
          src: imageType.src,
          declutterMode: imageType.declutterMode ?? 'declutter',
          scale: imageType.scale ?? 0.5
        })
    }
  }

  /**
   * 给8位字符串文件名补0
   * @param num
   * @param len
   * @param radix
   * @returns
   */
  private zeroFill(num: number, len: number, radix: number) {
    let str = num.toString(radix || 10)
    while (str.length < len) {
      str = '0' + str
    }
    return str
  }
}
